import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../lib/api.js';
import { useSession } from '../../lib/useSession.js';
import { Table, type Column } from '../../components/ui/Table.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { StatusPill } from '../../components/ui/StatusPill.js';
import { useToast } from '../../components/ui/Toast.js';
import './account.css';

interface GithubConnection {
  connected: boolean;
  githubLogin?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface SessionRow {
  id: string;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean }>('/auth/password', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      }),
    onSuccess: () => {
      setMsg({ kind: 'ok', text: 'Password changed. Other sessions were signed out.' });
      setCurrent('');
      setNext('');
      setConfirm('');
    },
    onError: (err) =>
      setMsg({ kind: 'err', text: err instanceof ApiError ? err.message : 'failed to change password' }),
  });

  const submit = () => {
    setMsg(null);
    if (next.length < 12) {
      setMsg({ kind: 'err', text: 'New password must be at least 12 characters.' });
      return;
    }
    if (next !== confirm) {
      setMsg({ kind: 'err', text: 'New password and confirmation do not match.' });
      return;
    }
    mut.mutate();
  };

  return (
    <section className="card account-card">
      <h2>Change password</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 360 }}>
        <input
          type="password"
          autoComplete="current-password"
          placeholder="Current password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <input
          type="password"
          autoComplete="new-password"
          placeholder="New password (min 12)"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <input
          type="password"
          autoComplete="new-password"
          placeholder="Confirm new password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {msg ? (
          <p className="dim" style={{ color: msg.kind === 'err' ? 'var(--danger, #e66)' : undefined }}>
            {msg.text}
          </p>
        ) : null}
        <button
          className="btn btn-amber"
          disabled={mut.isPending || !current || !next || !confirm}
          onClick={submit}
        >
          {mut.isPending ? 'Saving…' : 'Change password'}
        </button>
      </div>
    </section>
  );
}

export function AccountPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: me } = useSession();

  // ── github connection ──
  const { data: github } = useQuery<GithubConnection>({
    queryKey: ['github', 'connection'],
    queryFn: () => apiFetch<GithubConnection>('/github/connection'),
  });

  const disconnectMut = useMutation({
    mutationFn: () => apiFetch<{ ok: boolean }>('/github/connection', { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['github', 'connection'] });
      toast.success('GitHub disconnected');
    },
    onError: (err) => toast.error(errMessage(err, 'disconnect failed')),
  });

  // ── sessions ──
  const { data: sessions, isLoading: sessionsLoading } = useQuery<SessionRow[]>({
    queryKey: ['auth', 'sessions'],
    queryFn: () => apiFetch<SessionRow[]>('/auth/sessions'),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean }>(`/auth/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
      toast.success('Session revoked');
    },
    onError: (err) => toast.error(errMessage(err, 'revoke failed')),
  });

  // ── backup codes ──
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const regenMut = useMutation({
    mutationFn: () =>
      apiFetch<{ backupCodes: string[] }>('/auth/backup-codes/regenerate', { method: 'POST' }),
    onSuccess: (res) => setBackupCodes(res.backupCodes),
    onError: (err) => toast.error(errMessage(err, 'could not regenerate backup codes')),
  });

  const downloadCodes = () => {
    if (!backupCodes) return;
    const body = [
      'SherpaDeploy — 2FA Backup Codes',
      'Each code can be used once in place of an authenticator code.',
      '',
      ...backupCodes,
    ].join('\r\n');
    const blob = new Blob([body], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'sherpadeploy-2fa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const sessionColumns: Column<SessionRow>[] = [
    {
      key: 'ip',
      header: 'IP',
      render: (s) => <span className="mono">{s.ip ?? '—'}</span>,
    },
    {
      key: 'userAgent',
      header: 'Client',
      render: (s) => (
        <span className="account-ua" title={s.userAgent ?? undefined}>
          {s.userAgent ?? '—'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Signed in',
      render: (s) => <span className="ui-cell-mute">{fmtDate(s.createdAt)}</span>,
    },
    {
      key: 'lastSeenAt',
      header: 'Last seen',
      render: (s) => <span className="ui-cell-mute">{fmtDate(s.lastSeenAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (s) => (
        <button
          className="btn btn-ghost-danger btn-sm"
          disabled={revokeMut.isPending && revokeMut.variables === s.id}
          onClick={() => revokeMut.mutate(s.id)}
        >
          Revoke
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Account</h1>
          <p className="page-sub">Your profile, connections, and active sessions.</p>
        </div>
      </div>

      <div className="account-grid">
        {/* profile */}
        <section className="card account-card">
          <h2>Profile</h2>
          {me ? (
            <dl className="account-dl">
              <dt>Display name</dt>
              <dd>{me.displayName}</dd>
              <dt>Username</dt>
              <dd className="mono">{me.username}</dd>
              <dt>Email</dt>
              <dd className="mono">{me.email}</dd>
              <dt>Role</dt>
              <dd>
                <span className="badge">{me.role}</span>
              </dd>
              <dt>Two-factor</dt>
              <dd>
                {me.totpEnabled ? (
                  <StatusPill variant="ok">enabled</StatusPill>
                ) : (
                  <StatusPill variant="err">disabled</StatusPill>
                )}
              </dd>
            </dl>
          ) : (
            <p className="page-sub">Loading…</p>
          )}
        </section>

        <ChangePasswordCard />

        {/* github */}
        <section className="card account-card">
          <h2>GitHub</h2>
          {github?.connected ? (
            <>
              <div className="account-gh-row">
                <StatusPill variant="ok">connected</StatusPill>
                <span className="mono">{github.githubLogin}</span>
              </div>
              <p className="page-sub">Used to pull repositories for deployments.</p>
              <div className="account-card-actions">
                <button
                  className="btn btn-ghost-danger btn-sm"
                  disabled={disconnectMut.isPending}
                  onClick={() => disconnectMut.mutate()}
                >
                  {disconnectMut.isPending ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="account-gh-row">
                <StatusPill variant="neutral">not connected</StatusPill>
              </div>
              <p className="page-sub">
                Connect your GitHub account to deploy from your repositories.
              </p>
              <div className="account-card-actions">
                <a className="btn btn-amber" href="/api/v1/github/connect">
                  Connect GitHub
                </a>
              </div>
            </>
          )}
        </section>

        {/* security */}
        <section className="card account-card">
          <h2>Security</h2>
          <p className="page-sub">
            Regenerating backup codes invalidates all previous codes. The new codes are shown only
            once.
          </p>
          <div className="account-card-actions">
            <button
              className="btn btn-ghost"
              disabled={regenMut.isPending}
              onClick={() => regenMut.mutate()}
            >
              {regenMut.isPending ? 'Generating…' : 'Regenerate backup codes'}
            </button>
          </div>
        </section>
      </div>

      <h2 className="account-section-title">Active sessions</h2>
      <Table
        columns={sessionColumns}
        rows={sessions ?? []}
        rowKey={(s) => s.id}
        empty={sessionsLoading ? 'Loading…' : 'No active sessions.'}
      />

      {/* backup codes — shown exactly once */}
      <Dialog
        open={backupCodes !== null}
        onClose={() => setBackupCodes(null)}
        title="New backup codes"
      >
        <p className="dialog-desc">
          Store these somewhere safe — they are shown <strong>only once</strong>. Each works a
          single time if you lose your authenticator. Your previous codes no longer work.
        </p>
        <ul className="mono account-codes">
          {(backupCodes ?? []).map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={downloadCodes}>
            Download .txt
          </button>
          <button className="btn btn-amber" onClick={() => setBackupCodes(null)}>
            Done
          </button>
        </div>
      </Dialog>
    </div>
  );
}
